/**
 * EmojiPicker Component
 * Simple emoji picker dialog for selecting schedule icons.
 */
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  IconButton,
  Tooltip,
  Typography,
  Tabs,
  Tab,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

interface EmojiPickerProps {
  open: boolean;
  onClose: () => void;
  onSelect: (emoji: string) => void;
  currentEmoji?: string;
}

interface EmojiCategory {
  key: string;
  emojis: string[];
}

const EMOJI_CATEGORIES: EmojiCategory[] = [
  {
    key: 'activities',
    emojis: ['📋', '📅', '✅', '⏰', '🎯', '📝', '✏️', '🗓️', '📆', '🔔', '⚡', '🚀'],
  },
  {
    key: 'morning',
    emojis: ['🌅', '☀️', '🌤️', '🌞', '🍳', '🥐', '🥣', '☕', '🪥', '🚿', '🛁', '👕'],
  },
  {
    key: 'evening',
    emojis: ['🌙', '🌃', '⭐', '🛏️', '🌛', '😴', '📖', '🧸', '🎵', '🍵', '🪫', '💤'],
  },
  {
    key: 'school',
    emojis: ['📚', '✏️', '📓', '🎒', '🏫', '📐', '🖍️', '📒', '🎨', '🔬', '🌍', '🧮'],
  },
  {
    key: 'chores',
    emojis: ['🧹', '🧺', '🍽️', '🗑️', '🧽', '🧴', '🪣', '🧤', '👟', '🐕', '🌱', '🪴'],
  },
  {
    key: 'fun',
    emojis: ['🎮', '⚽', '🎨', '🎭', '🎬', '🎶', '📺', '🎪', '🎡', '🎢', '🏃', '🚴'],
  },
  {
    key: 'food',
    emojis: ['🍎', '🥗', '🍕', '🍔', '🍰', '🍿', '🥤', '🍪', '🍩', '🥪', '🌮', '🍱'],
  },
  {
    key: 'other',
    emojis: ['❤️', '⭐', '🌈', '🎁', '🏆', '🥇', '💪', '👍', '🤝', '💡', '🔑', '🎈'],
  },
];

export const EmojiPicker: React.FC<EmojiPickerProps> = ({
  open,
  onClose,
  onSelect,
  currentEmoji,
}) => {
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState(0);

  const handleEmojiClick = (emoji: string) => {
    onSelect(emoji);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('schedules.iconPicker.title')}</DialogTitle>
      <DialogContent>
        <Tabs
          value={selectedTab}
          onChange={(_, v) => setSelectedTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          {EMOJI_CATEGORIES.map((cat, idx) => (
            <Tab
              key={cat.key}
              label={t(`schedules.iconPicker.categories.${cat.key}`)}
              sx={{ minWidth: 'auto', px: 1.5 }}
            />
          ))}
        </Tabs>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(6, 1fr)',
            gap: 0.5,
          }}
        >
          {EMOJI_CATEGORIES[selectedTab].emojis.map((emoji) => (
            <Tooltip key={emoji} title={emoji} placement="top">
              <IconButton
                onClick={() => handleEmojiClick(emoji)}
                sx={{
                  fontSize: '1.5rem',
                  p: 1,
                  borderRadius: 1,
                  bgcolor: currentEmoji === emoji ? 'primary.light' : 'transparent',
                  '&:hover': {
                    bgcolor: currentEmoji === emoji ? 'primary.light' : 'action.hover',
                  },
                }}
              >
                {emoji}
              </IconButton>
            </Tooltip>
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
};
